import {
  DEFAULT_QA_SCREENSHOTS,
  DEFAULT_QA_RECORDING,
  MAX_QA_SCREENSHOT_COUNT,
  MAX_QA_SCREENSHOT_BYTES,
  MAX_QA_RECORDING_SECONDS,
  MAX_QA_RECORDING_BYTES,
  isQaEvidenceLimitsValid,
} from "@yagni/shared";
/**
 * The Configuration band: Application (with its tests and managed services),
 * Sign-in, First check, Credentials and Network. Everything the run inferred,
 * and everything it could not.
 */
import { useId, useState } from "react";
import {
  DEFAULT_QA_COMMAND_TIMEOUT_MS,
  isQaCommandTimeoutValid,
  DEFAULT_QA_READINESS_TIMEOUT_MS,
  QA_RUN_DEADLINE_MS,
  isQaReadinessTimeoutValid,
  type QaEnvironmentConfig,
  type WorkerConfigField,
} from "@yagni/shared";
import { Button, Input } from "../ui/index.js";
import {
  ConfigRow,
  LinesControl,
  Row,
  VariableRejects,
  parseDotEnv,
  type VariableReject,
} from "./environmentControls.js";
import { joinNames } from "./environmentPage.js";
import { EMPTY_COGNITO_AUTH, EnvironmentCognitoAuth } from "./EnvironmentCognitoAuth.js";
import { count } from "./EnvironmentConfigSummary.js";

export interface RepositoryChoice {
  id: string;
  owner: string;
  name: string;
}

type Patch = (patch: Partial<QaEnvironmentConfig>) => void;

const TARGET_FIELD: WorkerConfigField = {
  key: "qa-target",
  label: "Browser target",
  help: "Where the tested application runs.",
  type: "select",
  options: [
    { value: "sandbox", label: "Start the PR in a sandbox" },
    { value: "deployment", label: "Matching GitHub deployment preview" },
  ],
};
const ENTRY_FIELD: WorkerConfigField = {
  key: "qa-entry-path",
  label: "Browser entry path",
  help: "The first page the browser opens.",
  type: "text",
};
const AUTH_MODE_FIELD: WorkerConfigField = {
  key: "qa-auth-mode",
  label: "Authentication mode",
  help: "How the test identity gets signed in.",
  type: "select",
  // One unattended mode. "recipe" and "bootstrap" were older names for the
  // same thing; the schema reads them as "command", and so does `authMode`.
  options: [
    { value: "public", label: "Public application" },
    { value: "command", label: "Command" },
    { value: "human", label: "Human sign-in" },
    { value: "cognito", label: "AWS Cognito test identity" },
  ],
};
const AUTH_COMMAND_FIELD: WorkerConfigField = {
  key: "qa-auth-command",
  label: "Sign-in command",
  help: "Write Playwright storage state to the file named by QA_STORAGE_STATE. It runs before capture begins.",
  type: "text",
};
const VERIFY_FIELD: WorkerConfigField = {
  key: "qa-verify-command",
  label: "Test command",
  help: "The repository's own test command. Wright's builds run it to verify a change.",
  type: "text",
};
const BROWSER_TEST_FIELD: WorkerConfigField = {
  key: "qa-browser-test-command",
  label: "Browser test command",
  help: "Optional. An existing browser suite to run beside the check.",
  type: "text",
};

/** The managed services a run can start beside the app, and what each gives it. */
const MANAGED_SERVICES = [
  {
    key: "postgres",
    short: "PostgreSQL",
    label: "PostgreSQL 17 with vector support",
    help: "A fresh, empty database that starts before installation. The run supplies DATABASE_URL and the PG variables.",
    port: 55432,
  },
  {
    key: "redis",
    short: "Redis",
    label: "Redis",
    help: "A fresh Redis that starts before installation. The run supplies REDIS_URL, REDIS_HOST, REDIS_PORT and REDIS_PASSWORD.",
    port: 6379,
  },
  {
    key: "mysql",
    short: "MySQL",
    label: "MySQL",
    help: "A fresh, empty MySQL database that starts before installation. The run supplies the MYSQL variables.",
    port: 3306,
  },
] as const;
const AUTH_SELECTOR_FIELD: WorkerConfigField = {
  key: "qa-auth-selector",
  label: "Signed-in selector",
  help: "Visible only once the dedicated test identity has signed in.",
  type: "text",
};
const FIRST_SELECTOR_FIELD: WorkerConfigField = {
  key: "qa-first-selector",
  label: "First-check selector",
  help: "An element that proves the first useful page loaded. A captured page alone does not pass.",
  type: "text",
};
const FIRST_TEXT_FIELD: WorkerConfigField = {
  key: "qa-first-text",
  label: "Expected text",
  help: "Optional. Text that must appear inside that element.",
  type: "text",
};

/**
 * A bounded number: a short visible label, a narrow mono input and its unit
 * on one line. The accessible name stays the full phrase the tests and
 * screen readers know ("Startup wait for app (minutes)").
 */
function NumberField({
  label,
  accessibleLabel,
  unit,
  value,
  min,
  max,
  step,
  invalid,
  describedBy,
  onChange,
}: {
  label?: string;
  accessibleLabel: string;
  unit: string;
  value: number;
  min: number;
  max: number;
  step?: number | "any";
  invalid?: boolean;
  describedBy?: string;
  onChange: (raw: string) => void;
}) {
  return (
    <label className="qa-number">
      {label && <span className="qa-number-label">{label}</span>}
      <span className="qa-number-control">
        <Input
          inputSize="sm"
          type="number"
          min={min}
          max={max}
          step={step}
          value={value}
          aria-label={accessibleLabel}
          aria-describedby={describedBy}
          aria-invalid={invalid ? true : false}
          state={invalid ? "error" : "default"}
          onChange={(event) => onChange(event.target.value)}
        />
        <span className="qa-number-unit">{unit}</span>
      </span>
    </label>
  );
}

/** The sign-in mode as the page offers it: the older command names read as one. */
export function authMode(config: QaEnvironmentConfig): "public" | "command" | "human" | "cognito" {
  const mode = config.authMode ?? (config.authCommand ? "command" : "public");
  return mode === "recipe" || mode === "bootstrap" ? "command" : mode;
}

export function signInSummary(config: QaEnvironmentConfig): string {
  return {
    public: "Public pages only",
    command: config.authCommand ?? "Sign-in command",
    human: "Human sign-in",
    cognito: config.cognitoAuth?.userPoolId
      ? `AWS Cognito ${config.cognitoAuth.userPoolId}`
      : "AWS Cognito test identity",
  }[authMode(config)];
}

const DEFAULT_INSTALLATION_ORIGINS = ["https://registry.npmjs.org"];

/** The Network row folded: how many origins each list approves. */
export function networkSummary(config: QaEnvironmentConfig): string {
  const installs = (config.installationOrigins ?? DEFAULT_INSTALLATION_ORIGINS).filter(
    Boolean,
  ).length;
  const app = config.allowedOrigins.filter(Boolean).length;
  return `${count(installs, "installation origin")} · ${count(app, "application origin")}`;
}

/**
 * Network: the two allowlists a run's egress is built from. Installation
 * origins are for package downloads during setup; application origins are
 * where the browser and the running app may reach.
 */
export function NetworkFields({
  config,
  onChange,
}: {
  config: QaEnvironmentConfig;
  onChange: Patch;
}) {
  return (
    <>
      <Row
        title="Installation origins"
        help="Registries and package hosts the installation may reach. Browser pages cannot use these."
      >
        <span className="wrk-srow-controls">
          <LinesControl
            label="Installation origins"
            placeholder="https://registry.npmjs.org"
            value={config.installationOrigins ?? DEFAULT_INSTALLATION_ORIGINS}
            onCommit={(installationOrigins) => onChange({ installationOrigins })}
          />
        </span>
      </Row>
      <Row
        title="Application origins"
        help="Where the browser and the running app are allowed to go once it is up."
      >
        <span className="wrk-srow-controls">
          <LinesControl
            label="Application origins"
            placeholder="https://preview.example.com"
            value={config.allowedOrigins}
            onCommit={(allowedOrigins) => onChange({ allowedOrigins })}
          />
        </span>
      </Row>
    </>
  );
}

export function firstCheckSummary(config: QaEnvironmentConfig): string {
  return (
    config.firstCheck?.selector ?? "Nothing checked yet, so a reachable page is all this proves"
  );
}

export function ApplicationFields({
  config,
  repositories,
  primaryRepoId,
  onChange,
}: {
  config: QaEnvironmentConfig;
  repositories: RepositoryChoice[];
  primaryRepoId: string;
  onChange: Patch;
}) {
  const readinessHelpId = useId();
  const commandHelpId = useId();
  const dependencies = config.dependencies ?? [];
  const name = (id: string) => {
    const found = repositories.find((repo) => repo.id === id);
    return found ? `${found.owner}/${found.name}` : "Repository unavailable";
  };
  const update = (index: number, patch: Partial<QaEnvironmentConfig["services"][number]>) =>
    onChange({
      services: config.services.map((service, n) =>
        n === index ? { ...service, ...patch } : service,
      ),
    });
  return (
    <>
      <ConfigRow
        field={TARGET_FIELD}
        value={config.target}
        onChange={(value) => onChange({ target: value as QaEnvironmentConfig["target"] })}
      />
      {config.target === "deployment" && (
        <Row title="Deployment previews">
          <span className="wrk-srow-controls qa-note">
            A successful, non-production GitHub deployment must match the tested commit and an
            application origin approved under Network.
          </span>
        </Row>
      )}
      <Row title="Installation commands" help="Run once, in order, before anything starts.">
        <span className="wrk-srow-controls">
          <LinesControl
            label="Installation commands"
            value={config.setupCommands}
            onCommit={(setupCommands) => onChange({ setupCommands })}
          />
        </span>
      </Row>
      <Row
        title="Command wait"
        help={`How long each installation, test, or sign-in command may run. Up to ${QA_RUN_DEADLINE_MS / 60000} minutes, within the run deadline; clear to restore the ${DEFAULT_QA_COMMAND_TIMEOUT_MS / 60000}-minute default.`}
        helpId={`${commandHelpId}-help`}
      >
        <span className="wrk-srow-controls qa-numbers">
          <NumberField
            accessibleLabel="Command wait (minutes)"
            unit="min"
            min={1 / 60000}
            max={QA_RUN_DEADLINE_MS / 60000}
            step="any"
            describedBy={
              isQaCommandTimeoutValid(config.commandTimeoutMs)
                ? `${commandHelpId}-help`
                : commandHelpId
            }
            invalid={!isQaCommandTimeoutValid(config.commandTimeoutMs)}
            value={(config.commandTimeoutMs ?? DEFAULT_QA_COMMAND_TIMEOUT_MS) / 60000}
            onChange={(raw) => {
              const value = raw.trim() ? Math.round(Number(raw) * 60000) : undefined;
              onChange({
                commandTimeoutMs: value === DEFAULT_QA_COMMAND_TIMEOUT_MS ? undefined : value,
              });
            }}
          />
          {!isQaCommandTimeoutValid(config.commandTimeoutMs) && (
            <span id={commandHelpId} className="qa-number-alert" role="alert">
              Enter a command wait greater than zero and no more than {QA_RUN_DEADLINE_MS / 60000}{" "}
              minutes.
            </span>
          )}
        </span>
      </Row>
      {config.services.map((service, index) => (
        <Row
          key={index}
          title={service.name || `Service ${index + 1}`}
          help="What starts, where it starts, and how the runner knows it is up."
          testId={`qa-service-${index}`}
        >
          <span className="wrk-srow-controls qa-service">
            <label>
              Service name
              <Input
                value={service.name}
                onChange={(event) => update(index, { name: event.target.value })}
              />
            </label>
            <label>
              Start command for {service.name || `service ${index + 1}`}
              <Input
                value={service.command}
                onChange={(event) => update(index, { command: event.target.value })}
              />
            </label>
            <label>
              Working directory for {service.name || `service ${index + 1}`}
              <Input
                value={service.cwd}
                onChange={(event) => update(index, { cwd: event.target.value })}
              />
            </label>
            <label>
              Port for {service.name || `service ${index + 1}`}
              <Input
                type="number"
                min={1024}
                max={65535}
                value={service.port}
                onChange={(event) => update(index, { port: Number(event.target.value) })}
              />
            </label>
            <label>
              Readiness path for {service.name || `service ${index + 1}`}
              <Input
                value={service.readinessPath}
                onChange={(event) => update(index, { readinessPath: event.target.value })}
              />
            </label>
            <NumberField
              label="Startup wait"
              accessibleLabel={`Startup wait for ${service.name || `service ${index + 1}`} (minutes)`}
              unit="min"
              min={1 / 60000}
              max={QA_RUN_DEADLINE_MS / 60000}
              step="any"
              describedBy={`${readinessHelpId}-${index}`}
              invalid={!isQaReadinessTimeoutValid(service.readinessTimeoutMs)}
              value={(service.readinessTimeoutMs ?? DEFAULT_QA_READINESS_TIMEOUT_MS) / 60000}
              onChange={(raw) => update(index, { readinessTimeoutMs: Number(raw) * 60000 })}
            />
            <span
              id={`${readinessHelpId}-${index}`}
              className={
                isQaReadinessTimeoutValid(service.readinessTimeoutMs)
                  ? "wrk-srow-help"
                  : "qa-number-alert"
              }
              role={isQaReadinessTimeoutValid(service.readinessTimeoutMs) ? undefined : "alert"}
            >
              {isQaReadinessTimeoutValid(service.readinessTimeoutMs)
                ? `How long the runner waits for this service to answer on its readiness path. Setup and checks share a ${QA_RUN_DEADLINE_MS / 60000}-minute run limit.`
                : `Enter a startup wait greater than 0 and no more than ${QA_RUN_DEADLINE_MS / 60000} minutes.`}
            </span>
            {dependencies.length > 0 && (
              <label>
                Repository for service {service.name || index + 1}
                <select
                  value={service.repoId ?? primaryRepoId}
                  onChange={(event) =>
                    onChange({
                      services: config.services.map((item, itemIndex) => {
                        if (itemIndex !== index) return item;
                        const { repoId: _repoId, ...primaryService } = item;
                        return event.target.value === primaryRepoId
                          ? primaryService
                          : { ...item, repoId: event.target.value };
                      }),
                    })
                  }
                >
                  <option value={primaryRepoId}>{name(primaryRepoId)} (primary)</option>
                  {dependencies.map((dependency) => (
                    <option key={dependency.repoId} value={dependency.repoId}>
                      {name(dependency.repoId)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <Button
              variant="ghost"
              onClick={() =>
                onChange({
                  services: config.services.filter((_, n) => n !== index),
                })
              }
            >
              Remove {service.name || "service"}
            </Button>
          </span>
        </Row>
      ))}
      <Row title="Add a service" help="Up to eight start together.">
        <span className="wrk-srow-controls">
          <Button
            variant="secondary"
            disabled={config.services.length >= 8}
            onClick={() =>
              onChange({
                services: [
                  ...config.services,
                  {
                    name: `app${config.services.length + 1}`,
                    command: "",
                    cwd: ".",
                    port: 3000 + config.services.length,
                    readinessPath: "/",
                  },
                ],
              })
            }
          >
            Add service
          </Button>
        </span>
      </Row>
      <ConfigRow
        field={ENTRY_FIELD}
        value={config.entryPath}
        onChange={(value) => onChange({ entryPath: String(value) })}
      />

      <h3 className="qa-subhead">Tests</h3>
      <ConfigRow
        field={VERIFY_FIELD}
        value={config.verifyCommand ?? ""}
        onChange={(value) => onChange({ verifyCommand: String(value) || null })}
      />
      <ConfigRow
        field={BROWSER_TEST_FIELD}
        value={config.browserTestCommand ?? ""}
        onChange={(value) => onChange({ browserTestCommand: String(value) || null })}
      />

      <h3 className="qa-subhead">Services beside the app</h3>
      {MANAGED_SERVICES.map((service) => {
        const current = config[service.key];
        return (
          <Row
            key={service.key}
            title={service.label}
            help={service.help}
            testId={`qa-managed-${service.key}`}
          >
            <span className="wrk-srow-controls qa-numbers">
              <label className="qa-toggle">
                <input
                  type="checkbox"
                  checked={Boolean(current)}
                  onChange={(event) =>
                    onChange({
                      [service.key]: event.target.checked ? { port: service.port } : null,
                    })
                  }
                />
                <span>Start {service.short}</span>
              </label>
              {current && (
                <NumberField
                  label="Port"
                  accessibleLabel={`${service.short} port`}
                  unit=""
                  min={1024}
                  max={65535}
                  value={current.port}
                  onChange={(raw) => onChange({ [service.key]: { port: Number(raw) } })}
                />
              )}
            </span>
          </Row>
        );
      })}
    </>
  );
}

/**
 * Sign-in in one place: the mode, the command and selector it needs, and the
 * origins a human sign-in is allowed to touch. These used to sit in two
 * separate rows that contradicted each other.
 */
export function SignInFields({
  config,
  credentialNames,
  onChange,
  onOpenRow,
}: {
  config: QaEnvironmentConfig;
  credentialNames: string[];
  onChange: Patch;
  /** Opens another settings row: the ones the Cognito warnings name. */
  onOpenRow?: (section: string) => void;
}) {
  const mode = authMode(config);
  return (
    <>
      <ConfigRow
        field={AUTH_MODE_FIELD}
        value={mode}
        onChange={(value) => {
          const next = value as NonNullable<QaEnvironmentConfig["authMode"]>;
          onChange({
            authMode: next,
            ...(["public", "human", "cognito"].includes(next) ? { authCommand: null } : {}),
            // A configured pool is kept when the mode changes: its credential
            // names are what keep the test identity out of the application's
            // own commands.
            ...(next === "cognito" && !config.cognitoAuth
              ? { cognitoAuth: EMPTY_COGNITO_AUTH }
              : {}),
          });
        }}
      />
      {mode === "command" && (
        <ConfigRow
          field={AUTH_COMMAND_FIELD}
          value={config.authCommand ?? ""}
          onChange={(value) => onChange({ authCommand: String(value) || null })}
        />
      )}
      {mode !== "public" && (
        <ConfigRow
          field={AUTH_SELECTOR_FIELD}
          value={config.authCheckSelector ?? ""}
          onChange={(value) => onChange({ authCheckSelector: String(value) || null })}
        />
      )}
      {mode === "cognito" && (
        <EnvironmentCognitoAuth
          config={config}
          credentialNames={credentialNames}
          onChange={onChange}
          onOpenRow={onOpenRow}
        />
      )}
      {mode === "human" && (
        <>
          <Row
            title="Sign-in origins"
            help="Approve exact sign-in origins, one per line, without paths or wildcards."
          >
            <span className="wrk-srow-controls">
              <LinesControl
                label="Sign-in origins"
                placeholder="https://accounts.google.com"
                value={config.authOrigins ?? []}
                onCommit={(authOrigins) => onChange({ authOrigins })}
              />
            </span>
          </Row>
          <Row
            title="Application authentication state origins"
            help="Only application and backend origins whose signed-in state may transfer into testing. Google account cookies are excluded."
          >
            <span className="wrk-srow-controls">
              <LinesControl
                label="Application authentication state origins"
                placeholder="https://preview.example.com"
                value={config.authStateOrigins ?? []}
                onCommit={(authStateOrigins) => onChange({ authStateOrigins })}
              />
            </span>
          </Row>
          <Row title="How it works">
            <span className="wrk-srow-controls qa-note">
              Sign in privately in the browser, then continue testing. Sign-in pages are excluded
              from the recorded test.
            </span>
          </Row>
        </>
      )}
      {mode !== "human" && (
        <Row title="Session storage">
          <span className="wrk-srow-controls qa-note">
            Applications that keep authentication only in sessionStorage need a sign-in command.
          </span>
        </Row>
      )}
    </>
  );
}

export function FirstCheckFields({
  config,
  onChange,
}: {
  config: QaEnvironmentConfig;
  onChange: Patch;
}) {
  const updateCheck = (selector: string, text: string) =>
    onChange({
      firstCheck: selector || text ? { selector, ...(text ? { text } : {}) } : undefined,
    });
  return (
    <>
      <Row
        title="Browser service"
        help="Which application the browser opens. Deployment previews use the matched preview URL."
      >
        <span className="wrk-srow-controls">
          <select
            className="wrk-control"
            aria-label="Browser service"
            value={config.browserService ?? ""}
            disabled={config.services.length === 0}
            onChange={(event) => onChange({ browserService: event.target.value || undefined })}
          >
            <option value="">Choose the application to open</option>
            {config.services.map((service) => (
              <option key={service.name} value={service.name}>
                {service.name} · port {service.port}
              </option>
            ))}
          </select>
        </span>
      </Row>
      <ConfigRow
        field={FIRST_SELECTOR_FIELD}
        value={config.firstCheck?.selector ?? ""}
        onChange={(value) => updateCheck(String(value), config.firstCheck?.text ?? "")}
      />
      <ConfigRow
        field={FIRST_TEXT_FIELD}
        value={config.firstCheck?.text ?? ""}
        onChange={(value) => updateCheck(config.firstCheck?.selector ?? "", String(value))}
      />
    </>
  );
}

/**
 * "Paste a .env": every usable line becomes a staged, write-only credential,
 * the box empties so the values do not linger on screen, and every line it
 * could not use is listed with its reason (by line number and name, never by
 * value).
 */
export function EnvPaste({
  onStage,
  title = "Paste a .env",
  testId = "qa-env-paste",
  help = "Each NAME=value line becomes a credential. Values stay write-only once staged.",
  actionLabel = "Stage credentials",
  doneVerb = "Staged",
  nothingLine = "Nothing was staged.",
}: {
  onStage: (values: Record<string, string>) => void;
  /** The row's title, also the box's label. */
  title?: string;
  testId?: string;
  /** The row's help line; the workspace variables row says its own. */
  help?: string;
  actionLabel?: string;
  /** The status line's verb: "Staged API_KEY." or "Saved API_KEY.". */
  doneVerb?: string;
  nothingLine?: string;
}) {
  const [text, setText] = useState("");
  const [result, setResult] = useState<{
    staged: string[];
    rejects: VariableReject[];
  } | null>(null);
  return (
    <Row title={title} help={help} testId={testId}>
      <span className="wrk-srow-controls qa-variables">
        <textarea
          className="wrk-control wrk-control--multiline"
          aria-label={title}
          rows={4}
          placeholder={"API_KEY=...\nDATABASE_PASSWORD=..."}
          spellCheck={false}
          autoComplete="off"
          value={text}
          onChange={(event) => setText(event.target.value)}
        />
        <Button
          variant="secondary"
          disabled={!text.trim()}
          onClick={() => {
            const parsed = parseDotEnv(text);
            const staged = Object.keys(parsed.variables);
            if (staged.length) onStage(parsed.variables);
            setResult({ staged, rejects: parsed.rejects });
            setText("");
          }}
        >
          {actionLabel}
        </Button>
        {result && (
          <span role="status" className="qa-note">
            {result.staged.length ? `${doneVerb} ${joinNames(result.staged)}.` : nothingLine}
            {result.rejects.length ? ` ${count(result.rejects.length, "line")} not used:` : ""}
          </span>
        )}
        {result && (
          <VariableRejects rejects={result.rejects} label="Lines not used from the pasted .env" />
        )}
      </span>
    </Row>
  );
}

export function EvidenceFields({
  config,
  onChange,
}: {
  config: QaEnvironmentConfig;
  onChange: Patch;
}) {
  const helpId = useId();
  const screenshots = config.screenshots ?? DEFAULT_QA_SCREENSHOTS;
  const recording = config.recording ?? DEFAULT_QA_RECORDING;
  const valid = isQaEvidenceLimitsValid(config);
  const inRange = (value: number, min: number, max: number) =>
    Number.isInteger(value) && value >= min && value <= max;
  const setScreenshots = (patch: Partial<typeof screenshots>) => {
    const next = { ...screenshots, ...patch };
    onChange({
      screenshots:
        next.maxCount === DEFAULT_QA_SCREENSHOTS.maxCount &&
        next.maxBytes === DEFAULT_QA_SCREENSHOTS.maxBytes
          ? undefined
          : next,
    });
  };
  const setRecording = (patch: Partial<typeof recording>) => {
    const next = { ...recording, ...patch };
    onChange({
      recording:
        next.maxSeconds === DEFAULT_QA_RECORDING.maxSeconds &&
        next.maxBytes === DEFAULT_QA_RECORDING.maxBytes
          ? undefined
          : next,
    });
  };
  const mib = 1024 * 1024;
  return (
    <>
      <Row
        title="Screenshots"
        help={`Captures and storage kept for one run: up to ${MAX_QA_SCREENSHOT_COUNT.toLocaleString()} captures and ${MAX_QA_SCREENSHOT_BYTES / mib} MiB. Clear a field to restore its default.`}
      >
        <span className="wrk-srow-controls qa-numbers">
          <NumberField
            label="Count"
            accessibleLabel="Screenshot count"
            unit="captures"
            min={1}
            max={MAX_QA_SCREENSHOT_COUNT}
            value={screenshots.maxCount}
            describedBy={helpId}
            invalid={!inRange(screenshots.maxCount, 1, MAX_QA_SCREENSHOT_COUNT)}
            onChange={(raw) =>
              setScreenshots({
                maxCount: raw.trim() ? Number(raw) : DEFAULT_QA_SCREENSHOTS.maxCount,
              })
            }
          />
          <NumberField
            label="Storage"
            accessibleLabel="Screenshot storage (MiB)"
            unit="MiB"
            min={1}
            max={MAX_QA_SCREENSHOT_BYTES / mib}
            value={screenshots.maxBytes / mib}
            describedBy={helpId}
            invalid={!inRange(screenshots.maxBytes, mib, MAX_QA_SCREENSHOT_BYTES)}
            onChange={(raw) =>
              setScreenshots({
                maxBytes: raw.trim()
                  ? Math.round(Number(raw) * mib)
                  : DEFAULT_QA_SCREENSHOTS.maxBytes,
              })
            }
          />
        </span>
      </Row>
      <Row
        title="Recording"
        help={`Continuous video stops when either limit is reached: up to ${MAX_QA_RECORDING_SECONDS / 60} minutes and ${MAX_QA_RECORDING_BYTES / mib} MiB. Clear a field to restore its default.`}
      >
        <span className="wrk-srow-controls qa-numbers">
          <NumberField
            label="Duration"
            accessibleLabel="Recording duration (minutes)"
            unit="min"
            min={1}
            max={MAX_QA_RECORDING_SECONDS / 60}
            value={recording.maxSeconds / 60}
            describedBy={helpId}
            invalid={!inRange(recording.maxSeconds, 60, MAX_QA_RECORDING_SECONDS)}
            onChange={(raw) =>
              setRecording({
                maxSeconds: raw.trim()
                  ? Math.round(Number(raw) * 60)
                  : DEFAULT_QA_RECORDING.maxSeconds,
              })
            }
          />
          <NumberField
            label="Storage"
            accessibleLabel="Recording storage (MiB)"
            unit="MiB"
            min={8}
            max={MAX_QA_RECORDING_BYTES / mib}
            value={recording.maxBytes / mib}
            describedBy={helpId}
            invalid={!inRange(recording.maxBytes, 8 * mib, MAX_QA_RECORDING_BYTES)}
            onChange={(raw) =>
              setRecording({
                maxBytes: raw.trim()
                  ? Math.round(Number(raw) * mib)
                  : DEFAULT_QA_RECORDING.maxBytes,
              })
            }
          />
        </span>
      </Row>
      <Row title="Run boundaries">
        <span
          id={helpId}
          className={"wrk-srow-controls " + (valid ? "qa-note" : "qa-number-alert")}
          role={valid ? undefined : "alert"}
        >
          {valid
            ? `These are ceilings, not promised coverage. Setup and checks share a ${QA_RUN_DEADLINE_MS / 60000}-minute run deadline and the Team spending limit.`
            : "Enter evidence limits within the displayed ranges."}
        </span>
      </Row>
    </>
  );
}
